import { enhancedApi as webApi } from './web-api'
import type { OcppLogsGatewayApiArg, OcppLogsPage } from './web-api'

export type OcppLogsInfiniteApiArg = Omit<OcppLogsGatewayApiArg, 'page'> & {
    source: 'gateway' | 'relay'
}

const enhancedApi = webApi
    .enhanceEndpoints({
        endpoints: {
            authRegister: {
                invalidatesTags: [],
            },
            authRegisterPartnerCandidate: {
                invalidatesTags: [],
            },
            authRequestPasswordReset: {
                invalidatesTags: [],
            },
            poolGetChargePoints: {
                providesTags: ['Pool'],
            },
            chargePointAddToPool: {
                invalidatesTags: ['ChargePoint', 'Company', 'Pool'],
            },
            externalServerGetByChargePointId: {
                providesTags: ['ExternalServer', 'ChargePoint'],
            },
            nfcTagConnectToUser: {
                invalidatesTags: ['NfcTag', 'Company'],
            },
            nfcTagDisconnectFromUser: {
                invalidatesTags: ['NfcTag', 'Company'],
            },
            partnerGetAlarmsByPartnerId: {
                providesTags: ['Alarm'],
            },
            partnerSetAcknowledged: {
                invalidatesTags: ['Alarm'],
            },
            partnerGetCompaniesByPartnerId: {
                providesTags: ['Company'],
            },
            companyTransferBatchTransfer: {
                invalidatesTags: ['CompanyTransfer', 'Company', 'Partner'],
            },
            companyAddMeasurementDeviceToCompany: {
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled
                        dispatch(webApi.util.invalidateTags(['MeasurementDevices']))
                    } catch {
                        // The mutation consumer handles request errors.
                    }
                },
            },
            companyUpdateCompanySettings: {
                invalidatesTags: (_result, _error, { body }) =>
                    body.some((setting) => setting.name === 'BillingCountry') ? ['Company', 'Payment'] : ['Company'],
            },
            companyGetCompanyById: {
                providesTags: ['Company'],
            },
            companyUpdateUser: {
                invalidatesTags: ['Company', 'User'],
            },
            companyGetServicePartnerCodeInformation: {
                invalidatesTags: [],
            },
            partnerCreateServicePartnerInvitationCode: {
                invalidatesTags: ['Partner'],
            },
            partnerGetCurrentServicePartnerInvitationCode: {
                providesTags: () => ['Partner'],
            },
            partnerGetNfcTags: {
                providesTags: ['Partner'],
            },
            nfcTagCreate: {
                invalidatesTags: ['Company', 'NfcTag', 'Partner'],
            },
            nfcTagUpdate: {
                invalidatesTags: ['Company', 'NfcTag', 'Partner'],
            },
            nfcTagDelete: {
                invalidatesTags: ['Company', 'NfcTag', 'Partner'],
            },
            chargePointDeleteRejectedTag: {
                invalidatesTags: ['NfcTag'],
            },
            chargePointSetOwner: {
                invalidatesTags: ['User'],
            },
            partnerGetLoadbalancerGroupsPaged: {
                providesTags: ['Partner', 'LoadBalancerGroup'],
            },
            companyGetLoadbalancerGroupsPaged: {
                providesTags: ['Company', 'LoadBalancerGroup'],
            },
            loadBalancerGroupValidateChargePointImports: {
                invalidatesTags: [],
            },
            chargingSessionGetChargingSessionsForCurrentUser: {
                providesTags: ['ChargingSession', 'ChargePoint', 'User'],
            },
        },
    })
    .injectEndpoints({
        overrideExisting: true,
        endpoints: (build) => ({
            ocppLogsInfinite: build.infiniteQuery<OcppLogsPage, OcppLogsInfiniteApiArg, number>({
                query: ({ queryArg: { source, chargePointId, action = [], ...filters }, pageParam }) => {
                    const params = new URLSearchParams()
                    Object.entries({ ...filters, page: pageParam }).forEach(([name, value]) => {
                        if (value !== undefined) {
                            params.set(name, String(value))
                        }
                    })
                    // The log service takes one `action` parameter per action, not a comma-separated list
                    action.forEach((value) => params.append('action', value))

                    return { url: `/chargepoints/${chargePointId}/ocpp-logs/${source}`, params }
                },
                infiniteQueryOptions: {
                    initialPageParam: 1,
                    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
                        lastPage.pagination?.hasMore ? lastPageParam + 1 : undefined,
                },
                providesTags: ['OcppLogs'],
            }),
        }),
    })

export { enhancedApi }
